import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

const COLORS = {
  orange: '#E87E04',
  green: '#008751',
};

const BackendUrl = process.env.REACT_APP_Backend_Url;

export default function AddEditPromoCode() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage',
    value: '',
    minOrderAmount: '0',
    maxDiscount: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    maxUsage: '',
    maxUsagePerUser: '1',
    isActive: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const admin = JSON.parse(localStorage.getItem('AdminEcomme'));
    if (admin) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${admin.token}`;
    }

    if (isEditMode) {
      fetchPromoCode();
    }
  }, [id]);

  const fetchPromoCode = async () => {
    try {
      const response = await axios.get(
        `${BackendUrl}/api/promocodes/admin/${id}`,
        { withCredentials: true }
      );

      const promo = response.data.promoCode;
      setFormData({
        code: promo.code,
        description: promo.description || '',
        type: promo.type,
        value: promo.value.toString(),
        minOrderAmount: promo.minOrderAmount.toString(),
        maxDiscount: promo.maxDiscount?.toString() || '',
        startDate: new Date(promo.startDate).toISOString().split('T')[0],
        endDate: new Date(promo.endDate).toISOString().split('T')[0],
        maxUsage: promo.maxUsage?.toString() || '',
        maxUsagePerUser: promo.maxUsagePerUser.toString(),
        isActive: promo.isActive,
      });
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur de chargement',
        description: error.response?.data?.message || 'Impossible de charger le code promo pour modification.',
        variant: 'destructive',
      });
    }
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Le code est requis';
    } else if (formData.code.length < 3) {
      newErrors.code = 'Le code doit contenir au moins 3 caractères';
    }

    if (!formData.value || formData.value <= 0) {
      newErrors.value = 'La valeur doit être supérieure à 0';
    }

    if (formData.type === 'percentage' && formData.value > 100) {
      newErrors.value = 'Le pourcentage ne peut pas dépasser 100%';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'La date de fin est requise';
    }

    if (formData.startDate && formData.endDate && formData.endDate <= formData.startDate) {
      newErrors.endDate = 'La date de fin doit être après la date de début';
    }

    if (formData.maxUsagePerUser && formData.maxUsagePerUser < 1) {
      newErrors.maxUsagePerUser = 'Doit être au moins 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: 'Erreur de validation',
        description: 'Veuillez corriger les erreurs dans le formulaire',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        code: formData.code.toUpperCase().trim(),
        description: formData.description.trim(),
        type: formData.type,
        value: parseFloat(formData.value),
        minOrderAmount: parseFloat(formData.minOrderAmount) || 0,
        maxDiscount: formData.maxDiscount ? parseFloat(formData.maxDiscount) : null,
        startDate: formData.startDate,
        endDate: formData.endDate,
        maxUsage: formData.maxUsage ? parseInt(formData.maxUsage) : null,
        maxUsagePerUser: parseInt(formData.maxUsagePerUser),
        isActive: formData.isActive,
      };

      if (isEditMode) {
        await axios.put(
          `${BackendUrl}/api/promocodes/admin/${id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${BackendUrl}/api/promocodes/admin`,
          payload,
          { withCredentials: true }
        );
      }

      toast({
        title: 'Succès',
        description: `Code promo ${isEditMode ? 'modifié' : 'créé'} avec succès`,
      });

      navigate('/Admin/PromoCodes');
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description:
          error.response?.data?.message ||
          `Impossible de ${isEditMode ? 'modifier' : 'créer'} le code promo`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/Admin/PromoCodes')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>
        <h1 className="text-3xl font-bold">
          {isEditMode ? 'Modifier le code promo' : 'Nouveau code promo'}
        </h1>
        <p className="text-gray-600">
          {isEditMode
            ? 'Modifiez les informations du code promo'
            : 'Créez un nouveau code de réduction'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Informations de base */}
          <Card>
            <CardHeader>
              <CardTitle>Informations de base</CardTitle>
              <CardDescription>
                Définissez le code et sa description
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="code">Code promo *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) =>
                    handleChange('code', e.target.value.toUpperCase())
                  }
                  placeholder="Ex: PROMO2026"
                  className={errors.code ? 'border-red-500' : ''}
                  disabled={isEditMode}
                />
                {errors.code && (
                  <p className="text-sm text-red-500 mt-1">{errors.code}</p>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Description du code promo (optionnel)"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Réduction */}
          <Card>
            <CardHeader>
              <CardTitle>Configuration de la réduction</CardTitle>
              <CardDescription>
                Définissez le type et la valeur de la réduction
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type">Type de réduction *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => handleChange('type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                      <SelectItem value="fixed">Montant fixe (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="value">
                    Valeur * ({formData.type === 'percentage' ? '%' : '€'})
                  </Label>
                  <Input
                    id="value"
                    type="number"
                    step="0.01"
                    min="0"
                    max={formData.type === 'percentage' ? '100' : undefined}
                    value={formData.value}
                    onChange={(e) => handleChange('value', e.target.value)}
                    className={errors.value ? 'border-red-500' : ''}
                  />
                  {errors.value && (
                    <p className="text-sm text-red-500 mt-1">{errors.value}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="minOrderAmount">
                    Montant minimum de commande (€)
                  </Label>
                  <Input
                    id="minOrderAmount"
                    type="number"
                    min="0"
                    value={formData.minOrderAmount}
                    onChange={(e) =>
                      handleChange('minOrderAmount', e.target.value)
                    }
                  />
                </div>

                {formData.type === 'percentage' && (
                  <div>
                    <Label htmlFor="maxDiscount">
                      Réduction maximum (€)
                    </Label>
                    <Input
                      id="maxDiscount"
                      type="number"
                      min="0"
                      value={formData.maxDiscount}
                      onChange={(e) =>
                        handleChange('maxDiscount', e.target.value)
                      }
                      placeholder="Illimité si vide"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Dates et limites */}
          <Card>
            <CardHeader>
              <CardTitle>Période de validité et limites</CardTitle>
              <CardDescription>
                Définissez la période et les limites d'utilisation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Date de début *</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleChange('startDate', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="endDate">Date de fin *</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleChange('endDate', e.target.value)}
                    className={errors.endDate ? 'border-red-500' : ''}
                  />
                  {errors.endDate && (
                    <p className="text-sm text-red-500 mt-1">{errors.endDate}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="maxUsage">
                    Nombre max d'utilisations totales
                  </Label>
                  <Input
                    id="maxUsage"
                    type="number"
                    min="1"
                    value={formData.maxUsage}
                    onChange={(e) => handleChange('maxUsage', e.target.value)}
                    placeholder="Illimité si vide"
                  />
                </div>

                <div>
                  <Label htmlFor="maxUsagePerUser">
                    Nombre max par utilisateur *
                  </Label>
                  <Input
                    id="maxUsagePerUser"
                    type="number"
                    min="1"
                    value={formData.maxUsagePerUser}
                    onChange={(e) =>
                      handleChange('maxUsagePerUser', e.target.value)
                    }
                    className={errors.maxUsagePerUser ? 'border-red-500' : ''}
                  />
                  {errors.maxUsagePerUser && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.maxUsagePerUser}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    handleChange('isActive', checked)
                  }
                />
                <Label htmlFor="isActive">Code actif</Label>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/Admin/PromoCodes')}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              style={{ backgroundColor: COLORS.orange }}
            >
              {isLoading ? (
                'Enregistrement...'
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isEditMode ? 'Mettre à jour' : 'Créer'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
